import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppDispatch } from '../../store';
import { saveCover } from '../../store/coverSlice';
import {
  comabooksCovers,
  COMABOOKS_DEFAULT_COVER_ID,
} from '../../components/ComabooksCover';

import { CountryPicker } from '../../components/CountryPicker/CountryPicker';
import marginLogo from '../../assets/comabooks-black.svg';
import { BRAND } from '../../brand';
import '../../styles/margin.css';
import './Onboarding.css';

/** Comabooks: выбор из 24 пресетов старого продукта (картинки-превью). */
interface CoverEntry {
  id: string;
  url: string;
}
const coverCatalog: CoverEntry[] = comabooksCovers.map((c) => ({ id: c.id, url: c.preview }));

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface OnboardingProps {
  templateId: number;
  recipient: string | null | undefined;
  onComplete: () => void;
}

type Step = 0 | 1 | 2 | 3 | 4 | 5;

export const Onboarding: React.FC<OnboardingProps> = ({
  templateId,
  recipient,
  onComplete,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation('onboarding');

  const [step, setStep] = useState<Step>(0);
  const [bookTitle, setBookTitle] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [selectedPainting, setSelectedPainting] = useState<CoverEntry>(
    coverCatalog.find((c) => c.id === COMABOOKS_DEFAULT_COVER_ID) ?? coverCatalog[0],
  );
  const [country, setCountry] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);

  // «девушка» и «парень» унифицированы под «вторая половинка» (см. TemplatePicker
  // — там тоже один пункт в дропдауне). На бэке шаблон остаётся прежний.
  const PARTNER_RECIPIENTS = ['девушка', 'парень'];
  const recipientDisplay = recipient
    ? PARTNER_RECIPIENTS.includes(recipient)
      ? (t('partner_label') as string)
      : recipient
    : (t('default_recipient') as string);

  const next = () => setStep((s) => (Math.min(5, s + 1) as Step));
  const back = () => setStep((s) => (Math.max(0, s - 1) as Step));

  // ============== step 3 carousel ==============

  const scrollerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Лок на время программного smooth-scroll — иначе scroll-handler ловит
  // промежуточные позиции и срывает выбор обратно на соседа.
  const programmaticScrollRef = useRef(false);
  const programmaticTimerRef = useRef<number | null>(null);

  // Утилита: smooth-scroll к выбранному id с защитой от scroll-handler'а.
  const scrollToPaintingId = (id: string) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const idx = coverCatalog.findIndex((p) => p.id === id);
    const target = itemRefs.current[idx];
    if (!target) return;
    const offset =
      target.offsetLeft + target.clientWidth / 2 - scroller.clientWidth / 2;

    programmaticScrollRef.current = true;
    if (programmaticTimerRef.current) {
      window.clearTimeout(programmaticTimerRef.current);
    }
    scroller.scrollTo({ left: offset, behavior: 'smooth' });
    // Snap window — у браузера smooth-scroll занимает ~400-600мс.
    programmaticTimerRef.current = window.setTimeout(() => {
      programmaticScrollRef.current = false;
      programmaticTimerRef.current = null;
    }, 600);
  };

  // Авто-скролл к текущей картинке только при входе на step 3 (mount).
  // На последующие изменения selectedPainting.id — не реагируем, иначе
  // получается петля «пользовательский скролл → setSelected → авто-скролл
  // обратно». Клик по картинке руками вызывает scrollToPaintingId.
  useEffect(() => {
    if (step !== 3) return;
    scrollToPaintingId(selectedPainting.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (step !== 3) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let raf = 0;
    const onScroll = () => {
      if (programmaticScrollRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = scroller.scrollLeft + scroller.clientWidth / 2;
        let closestIdx = 0;
        let closestDist = Infinity;
        itemRefs.current.forEach((el, i) => {
          if (!el) return;
          const mid = el.offsetLeft + el.clientWidth / 2;
          const dist = Math.abs(mid - center);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        });
        const next = coverCatalog[closestIdx];
        if (next) {
          setSelectedPainting((prev) => (prev.id === next.id ? prev : next));
        }
      });
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      scroller.removeEventListener('scroll', onScroll);
    };
  }, [step]);

  // ============== step 5 — save & loading ==============

  useEffect(() => {
    if (step !== 5) return;
    let mounted = true;
    const minDelay = new Promise((r) => setTimeout(r, 3500));

    const save = async () => {
      setSaveError(null);
      try {
        await dispatch(
          saveCover({
            templateId,
            authorName,
            bookTitle,
            coverUrl: selectedPainting.id,
            // Comabooks: receiver_name — имя второго человека, оно печатается
            // на обложке. Категорию шаблона сюда не пишем; имя клиент
            // вводит на странице обложки.
            receiverName: '',
          }),
        );
        const hasAnyContactData = country || city || address;
        if (hasAnyContactData) {
          const token = localStorage.getItem('token');
          await fetch(`${API_URL}/contacts`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              country: country || null,
              city: city || '',
              address: address || '',
              status: 'inProccess',
            }),
          });
        }
      } catch (err) {
        console.error('onboarding save failed', err);
        if (mounted) setSaveError(t('save_error') as string);
      }
    };

    Promise.all([save(), minDelay]).then(() => {
      if (!mounted) return;
      if (!saveError) onComplete();
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ============== step 0 — recipient ==============

  const renderRecipient = () => (
    <div className="mg-onb__step">
      <div className="mg-onb__kicker">{t('step1_kicker')}</div>
      <h1 className="mg-onb__title">
        {t('step1_title_prefix')} <em>{recipientDisplay}</em>{t('step1_title_suffix')}
      </h1>
      <p className="mg-onb__note">{t('step1_note')}</p>
      <div className="mg-onb__actions">
        <button className="mg-btn" onClick={next} type="button">
          {t('step1_confirm')}
        </button>
      </div>
    </div>
  );

  // ============== step 1 — book title ==============

  const renderTitle = () => (
    <div className="mg-onb__step">
      <div className="mg-onb__kicker">{t('step2_kicker')}</div>
      <h1 className="mg-onb__title">{t('step2_title')}</h1>
      <input
        type="text"
        className="mg-onb__input"
        placeholder={t('step2_placeholder') as string}
        value={bookTitle}
        onChange={(e) => {
          if (e.target.value.length <= 30) setBookTitle(e.target.value);
        }}
        autoFocus
      />
      <p className="mg-onb__note">{t('step2_note', { len: bookTitle.length })}</p>
      <div className="mg-onb__actions">
        <button className="mg-btn mg-btn--ghost" onClick={back} type="button">
          {t('back')}
        </button>
        <button
          className="mg-btn mg-btn--ghost"
          onClick={() => { setBookTitle(''); next(); }}
          type="button"
        >
          {t('skip')}
        </button>
        <button
          className="mg-btn"
          onClick={next}
          disabled={!bookTitle.trim()}
          type="button"
        >
          {t('next')}
        </button>
      </div>
    </div>
  );

  // ============== step 2 — author name ==============

  const renderAuthor = () => (
    <div className="mg-onb__step">
      <div className="mg-onb__kicker">{t('step3_kicker')}</div>
      <h1 className="mg-onb__title">{t('step3_title')}</h1>
      <input
        type="text"
        className="mg-onb__input"
        placeholder={t('step3_placeholder') as string}
        value={authorName}
        onChange={(e) => setAuthorName(e.target.value)}
        autoFocus
      />
      <p className="mg-onb__note">{t('step3_note')}</p>
      <div className="mg-onb__actions">
        <button className="mg-btn mg-btn--ghost" onClick={back} type="button">
          {t('back')}
        </button>
        <button
          className="mg-btn mg-btn--ghost"
          onClick={() => { setAuthorName(''); next(); }}
          type="button"
        >
          {t('skip')}
        </button>
        <button
          className="mg-btn"
          onClick={next}
          disabled={!authorName.trim()}
          type="button"
        >
          {t('next')}
        </button>
      </div>
    </div>
  );

  // ============== step 3 — painting carousel ==============

  const renderPainting = () => (
    <div className="mg-onb__step mg-onb__step--wide">
      <div className="mg-onb__kicker">{t('step4_kicker')}</div>
      <h1 className="mg-onb__title">{t('step4_title')}</h1>

      <div className="mg-onb__carousel-wrap">
        <div className="mg-onb__carousel" ref={scrollerRef}>
          <div className="mg-onb__carousel-pad" />
          {coverCatalog.map((p, i) => {
            const isActive = p.id === selectedPainting.id;
            return (
              <button
                key={p.id}
                ref={(el) => { itemRefs.current[i] = el; }}
                className={`mg-onb__paint mg-onb__paint--cb ${isActive ? 'mg-onb__paint--active' : ''}`}
                onClick={() => {
                  setSelectedPainting(p);
                  scrollToPaintingId(p.id);
                }}
                type="button"
              >
                <img src={p.url} alt="" />
              </button>
            );
          })}
          <div className="mg-onb__carousel-pad" />
        </div>
      </div>

      <p className="mg-onb__note">{t('step4_note')}</p>

      <div className="mg-onb__actions">
        <button className="mg-btn mg-btn--ghost" onClick={back} type="button">
          {t('back')}
        </button>
        <button className="mg-btn" onClick={next} type="button">
          {t('next')}
        </button>
      </div>
    </div>
  );

  // ============== step 4 — delivery date ==============

  const renderDate = () => {
    return (
      <div className="mg-onb__step">
        <h1 className="mg-onb__title">{t('step5_title')}</h1>

        <div className="mg-onb__contacts">
          <div className="mg-field">
            <label className="mg-field__label">{t('step5_field_country')}</label>
            <CountryPicker
              value={country}
              onChange={setCountry}
              placeholder={t('step5_country_placeholder') as string}
            />
          </div>

          <div className="mg-field">
            <label className="mg-field__label">{t('step5_field_city')}</label>
            <input
              type="text"
              className="mg-input"
              placeholder={t('step5_city_placeholder') as string}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>

          <div className="mg-field">
            <label className="mg-field__label">{t('step5_field_address')}</label>
            <input
              type="text"
              className="mg-input"
              placeholder={t('step5_address_placeholder') as string}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
        </div>

        <div className="mg-onb__actions">
          <button className="mg-btn mg-btn--ghost" onClick={back} type="button">
            {t('back')}
          </button>
          <button className="mg-btn" onClick={next} type="button">
            {t('step5_submit')}
          </button>
        </div>
      </div>
    );
  };

  // ============== step 5 — loading ritual ==============

  const renderLoading = () => (
    <div className="mg-onb__step mg-onb__step--ritual">
      <img src={marginLogo} alt="comabooks" className="mg-onb__ritual-logo" />
      <div className="mg-onb__ritual-title">{t('ritual_title')}</div>
      {saveError && (
        <>
          <p className="mg-onb__note mg-onb__error">{saveError}</p>
          <button className="mg-btn" onClick={() => setStep(4)} type="button">
            {t('ritual_back')}
          </button>
        </>
      )}
    </div>
  );

  const stepRenderers: Record<Step, () => React.ReactElement> = useMemo(
    () => ({
      0: renderRecipient,
      1: renderTitle,
      2: renderAuthor,
      3: renderPainting,
      4: renderDate,
      5: renderLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, bookTitle, authorName, selectedPainting, country, city, address, saveError],
  );

  return (
    <div className="mg-onb">
      <div className="mg-onb__bar">
        <span className="mg-onb__bar-logo">{BRAND.wordmark}</span>
        <div className="mg-onb__progress">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`mg-onb__pip ${step >= i ? 'mg-onb__pip--done' : ''}`}
            />
          ))}
        </div>
      </div>

      <div className="mg-onb__stage">{stepRenderers[step]()}</div>
    </div>
  );
};
